// Ce que l'étape 11 dicte — les quatre écrans de COL MNC, dans l'ordre où on
// les remonte.
//
// L'étape 11 NE COLLECTE RIEN. Tout ce qui est ici a été saisi plus tôt, dans
// l'étape qui l'a produit. Si un chiffre manque, il se corrige là-bas — jamais
// ici, sinon la donnée existe à deux endroits et plus personne ne sait laquelle
// fait foi.
//
// L'ordre à l'écran est l'INVERSE de celui de COL MNC : Kamel remonte ses
// écrans du bas vers le haut. Paramétrage, autres opérations, écarts de caisse,
// section coffre.
//
// Rien de ce qui est calculé ailleurs n'est recopié ici : les regroupements de
// bons viennent de `computeRemiseMensuelle` (sotcp-core), les totaux du coffre
// de `especes.ts`, les apports SCO de `sco.ts`. Une liste dupliquée finirait par
// diverger du code, et c'est la liste qu'on croirait.

import { computeRemiseMensuelle } from 'sotcp-core/csvParser'
import {
  BILLET_KEY, PIECE_DENOMS, PIECE_KEY, ROULEAU_VALUES, sumEnveloppesJ1,
} from 'sotcp-core/inventaireCoffreCalc'
import { sumApport } from 'sotcp-core/inventaireCoffreCalc'
import { consoliderBillets, lirePrelevSco } from './especes'
import type { Rapport } from './types'

const cents = (n: number): number => +(Number(n) || 0).toFixed(2)

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
const tableau = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : []

export interface LigneDictee {
  /** Ce qu'on lit à gauche — un nom, une dénomination, un poste. */
  cle: string
  /** D'où sort le chiffre. L'étape 11 ne demande jamais de faire confiance. */
  source?: string
  /**
   * COMBIEN d'objets derrière le montant — « 31 bons », « 2 PAD ».
   *
   * Séparé de `source` parce que ce n'est pas de la provenance : c'est une
   * donnée qu'on lit, et parfois qu'on saisit. Noyée dans la petite ligne grise
   * du dessous, elle se cherchait ; elle a maintenant sa place à elle, à côté
   * du montant.
   */
  compte?: string
  /** La valeur à recopier. En euros sauf si `nombre` est vrai. */
  valeur: number
  /** La valeur est un COMPTE (pièces, billets, enveloppes), pas un montant. */
  nombre?: boolean
  /** Un écart : couleur et signe obéissent à @/domain/ecart, jamais au signe. */
  ecart?: boolean
  /** Du texte à recopier tel quel — un commentaire, un motif. */
  texte?: string
  /**
   * Ce qui a produit la ligne. L'écran n'en fait rien — il les montre toutes —
   * mais le papier de saisie ne retient les écarts de CAISSE qu'au-delà de 2 €,
   * là où un règlement non conforme se saisit quel que soit son montant.
   * Trier sur le libellé de `source` marcherait aujourd'hui et casserait le
   * jour où quelqu'un reformule la phrase.
   */
  nature?: 'caisse' | 'reglement'
  /**
   * Une seconde valeur sur la même ligne. COL MNC demande, pour une même
   * dénomination, la quantité de PIÈCES et celle de ROULEAUX : deux lignes par
   * dénomination faisaient seize lignes là où huit disent la même chose.
   */
  paire?: { libelle: string; valeur: number; source?: string }
}

export interface BlocDictee { titre: string; sous?: string; lignes: LigneDictee[] }
export interface SectionDictee { id: string; titre: string; sous: string; blocs: BlocDictee[] }

export interface Caissier { badge: number | string; nom: string; prenom: string }

/**
 * Un dossier de la file d'attente PAD.
 *
 * DEUX JEUX DE NOMS, ET CE N'EST PAS UN CONFORT. La table `fc_pad_attente`
 * porte `pad_ref` et `montant_attendu` ; les premiers tests — écrits sur le
 * modèle de la V2 — parlaient de `reference` et `montant`. L'étape 11 passait
 * l'objet réel derrière un `as unknown as`, qui a fait taire le compilateur :
 * `lad()` lisait donc `reference` (absent) et `montant` (absent → 0), et
 * l'annexe LAD affichait des références vides pour un total de zéro. Invisible
 * tant que la file l'était aussi.
 *
 * On accepte les deux et on lit celui qui est là. Renommer d'un côté aurait
 * cassé l'autre en silence, une seconde fois.
 */
export interface PadEnAttente {
  pad_ref?: string
  reference?: string
  montant_attendu?: number
  montant?: number
  journee_origine: string
  statut: string
}

const refDossier = (p: PadEnAttente): string => String(p.pad_ref ?? p.reference ?? '—')
const montantDossier = (p: PadEnAttente): number =>
  Number(p.montant_attendu ?? p.montant ?? 0) || 0

// ── 1 · Paramétrage — apports en fonds de caisse flottants ─────────────────

/**
 * Une ligne par caissier ou par monnayeur, avec le TOTAL de la personne ou de
 * la machine. Rien d'autre : ni détail des dénominations, ni décomposition —
 * c'est ce que COL MNC attend, et le détail vit déjà aux étapes 02 et 02A.
 *
 * Le caissier est désigné par son BADGE, pas par son nom : c'est ce que COL MNC
 * demande. Un apport dont le badge est introuvable garde son nom plutôt que de
 * disparaître — un apport qu'on ne voit pas est un apport qu'on ne saisit pas.
 */
export function apportsFlottants(r: Rapport | null, caissiers: Caissier[]): LigneDictee[] {
  const parNom = new Map(caissiers.map((c) => [`${c.prenom} ${c.nom}`.trim(), c]))
  // Les caissiers d'abord, les monnayeurs ensuite — comme sur l'exemple de
  // Kamel (« 4596 : 50,00   850 : 15,50 »). Un tri unique sur les deux les
  // entremêlerait, et un badge se cherche mal au milieu de numéros de machine.
  const lignes: LigneDictee[] = []
  const machines: LigneDictee[] = []

  for (const [nom, v] of Object.entries(objet(r?.apports))) {
    const total = cents(sumApport(objet(v) as { rouleaux?: Record<string, unknown>; billets?: Record<string, unknown> }))
    if (total === 0) continue
    const op = parNom.get(nom)
    lignes.push({
      cle: op ? String(op.badge) : nom,
      source: op ? nom : 'badge introuvable aux réglages',
      valeur: total,
    })
  }

  for (const v of Object.values(objet(r?.apportsSco))) {
    const m = objet(v)
    const total = cents(Number(m.valeur) || 0)
    if (total === 0) continue
    machines.push({
      cle: m.monnayeur == null ? `SCO ${String(m.caisse ?? '?')}` : String(m.monnayeur),
      source: `monnayeur de la caisse ${String(m.caisse ?? '?')}`,
      valeur: total,
    })
  }

  const parCle = (a: LigneDictee, b: LigneDictee) =>
    a.cle.localeCompare(b.cle, 'fr', { numeric: true })
  return [...lignes.sort(parCle), ...machines.sort(parCle)]
}

// ── 2 · Autres opérations ──────────────────────────────────────────────────

const entrees = (r: Rapport | null): Record<string, unknown>[] =>
  tableau(objet(r?.inventaireCoffre).entrees_coffre)

/** Livraisons de monnaie : le total des entrées saisies à l'étape 10. */
export function livraisonsMonnaie(r: Rapport | null): { total: number; nb: number } {
  const l = entrees(r).filter((e) => String(e.type) === 'livraison')
  return { nb: l.length, total: cents(l.reduce((s, e) => s + (Number(e.amount) || 0), 0)) }
}

/** Argent trouvé : un montant et un commentaire par entrée. */
export function argentTrouve(r: Rapport | null): LigneDictee[] {
  return entrees(r)
    .filter((e) => String(e.type) === 'trouve')
    .map((e) => ({
      cle: String(e.label ?? '').trim() || 'sans commentaire',
      source: 'entrée de coffre — étape 10',
      valeur: cents(Number(e.amount) || 0),
    }))
}

// ── 3 · Écarts de caisse ───────────────────────────────────────────────────

/**
 * Une ou plusieurs lignes par personne ou par monnayeur. Un caissier peut
 * porter plusieurs POSTES DISTINCTS — un écart de caisse de −5,00 € et un bon
 * Catalina de +0,50 € ne sont pas deux justifications du même écart.
 *
 * Deux natures de ligne :
 *   • l'écart de caisse lui-même, du rapport opérateur, avec le commentaire
 *     saisi au dépouillement ;
 *   • les règlements déclarés NON CONFORMES, avec leur note — ce sont les
 *     lignes « bon Catalina », « chèque refusé » et compagnie.
 */
export function ecartsDeCaisse(r: Rapport | null): LigneDictee[] {
  const etats = objet(r?.rapportState)
  const verifs = objet(r?.verifications)
  const notes = objet(r?.notes)
  const lignes: LigneDictee[] = []

  for (const o of tableau(r?.rapportOps)) {
    const id = String(o.id ?? o.operateur ?? '')
    const diff = cents(Number(o.diff) || 0)
    if (diff === 0) continue
    const etat = objet(etats[id])
    const com = typeof etat.commentaireEcart === 'string' ? etat.commentaireEcart.trim() : ''
    lignes.push({
      cle: String(o.operateur ?? o.nom ?? id),
      source: 'écart de caisse — rapport opérateur',
      valeur: diff,
      ecart: true,
      texte: com,
      nature: 'caisse',
    })
  }

  for (const p of tableau(r?.payments)) {
    const id = String(p.id ?? '')
    // Convention V2 : `null` = validé, clé absente = jamais vu. Seul
    // « non_conforme » est un poste à porter en écart.
    if (verifs[id] !== 'non_conforme' && verifs[id] !== 'rejected') continue
    const note = typeof notes[id] === 'string' ? (notes[id] as string).trim() : ''
    lignes.push({
      cle: String(p.operateur ?? '—'),
      source: `${String(p.typeReglement ?? 'règlement')} · ticket ${String(p.ticket ?? '—')}`,
      valeur: cents(Number(p.montant) || 0),
      ecart: true,
      texte: note,
      nature: 'reglement',
    })
  }

  return lignes
}

// ── 4 · Section coffre ─────────────────────────────────────────────────────

/**
 * Le coffre monnaie dans la forme attendue par COL MNC : pour chaque
 * dénomination de pièce, la quantité de PIÈCES et la quantité de ROULEAUX
 * — deux comptes distincts, pas un montant.
 */
export function coffreMonnaie(r: Rapport | null): {
  pieces: LigneDictee[]
  billets: LigneDictee[]
  fixes: LigneDictee[]
  sorties: LigneDictee[]
  entrees: LigneDictee[]
} {
  const inv = objet(r?.inventaireCoffre)
  const q = (src: unknown, cle: string) => Math.max(0, Math.round(Number(objet(src)[cle]) || 0))

  const pieces: LigneDictee[] = []
  for (const d of PIECE_DENOMS as number[]) {
    const cle = PIECE_KEY(d)
    const nbPieces = q(inv.pieces, cle)
    const nbRouleaux = q(inv.rouleaux, cle)
    if (nbPieces === 0 && nbRouleaux === 0) continue
    pieces.push({
      cle: `${cle.replace('.', ',')} €`, valeur: nbPieces, nombre: true,
      source: `pièces · ${(nbPieces * d).toFixed(2).replace('.', ',')} €`,
      paire: {
        libelle: 'rouleaux', valeur: nbRouleaux,
        source: `${(nbRouleaux * (ROULEAU_VALUES[cle] ?? 0)).toFixed(2).replace('.', ',')} €`,
      },
    })
  }

  // Les billets sont CONSOLIDÉS : comptés en vrac plus ceux des enveloppes
  // scellées du vidage SCO, qui sont dans le coffre sans être dans le comptage.
  const cons = consoliderBillets(objet(inv.billets) as Record<string, number>,
                                lirePrelevSco(r)?.enveloppes ?? [])
  const billets: LigneDictee[] = cons.lignes
    .filter((l) => l.total > 0)
    .map((l) => ({
      cle: `${l.denomination} €`, valeur: l.total, nombre: true,
      source: l.enveloppes > 0
        ? `${l.comptes} comptés + ${l.enveloppes} en enveloppes · ${l.valeur.toFixed(2).replace('.', ',')} €`
        : `${l.valeur.toFixed(2).replace('.', ',')} €`,
    }))

  const fixes: LigneDictee[] = tableau(inv.caisse_fixes).map((f) => ({
    cle: String(f.name ?? f.id ?? '—'),
    source: 'fonds de caisse fixe — étape 10',
    valeur: cents(Number(f.confirmed_amount) || 0),
  }))

  // Seul le prélèvement du JOUR porte un numéro de scellé — celui préparé pour
  // demain n'en a pas encore (réponse de Kamel au §8.3 du handoff). Une
  // enveloppe sans numéro se dit quand même : c'est un manque à combler, pas
  // une ligne à cacher.
  const envJ = tableau(inv.prelevements_j)
  const sorties: LigneDictee[] = envJ.map((e, i) => ({
    cle: String(e.numero ?? '').trim() || `Enveloppe ${i + 1} — n° non saisi`,
    source: `prélèvement du jour · ${cents(Number(e.amount) || 0).toFixed(2).replace('.', ',')} €`,
    valeur: cents(Number(e.amount) || 0),
  }))
  if (envJ.length > 0) {
    sorties.push({
      cle: 'Total des prélèvements du jour',
      source: `${envJ.length} enveloppe${envJ.length > 1 ? 's' : ''} sortie${envJ.length > 1 ? 's' : ''} du coffre`,
      valeur: cents(sumEnveloppesJ1(envJ as Array<{ amount?: number }>)),
    })
  }

  const entreesLignes: LigneDictee[] = tableau(inv.entrees_coffre).map((e) => ({
    cle: String(e.label ?? '').trim() || String(e.type ?? 'entrée'),
    source: `type ${String(e.type ?? '—')}`,
    valeur: cents(Number(e.amount) || 0),
  }))

  return { pieces, billets, fixes, sorties, entrees: entreesLignes }
}

/**
 * LAD — les PAD encore en attente, TOUTES JOURNÉES CONFONDUES, y compris ceux
 * reportés des jours précédents. Pas les PAD en écart : uniquement la file
 * d'attente. Un dossier résolu ou basculé en écart n'y figure plus.
 */
export function lad(padAttente: PadEnAttente[]): { lignes: LigneDictee[]; total: number; nb: number } {
  const ouverts = padAttente.filter((p) => p.statut === 'en_attente')
  return {
    nb: ouverts.length,
    total: cents(ouverts.reduce((s, p) => s + montantDossier(p), 0)),
    lignes: ouverts.map((p) => ({
      cle: refDossier(p),
      source: `ouvert le ${p.journee_origine}`,
      valeur: cents(montantDossier(p)),
    })),
  }
}

/** Chèques manuels — nombre et total. `CHQ MANUEL` est le règlement 3. */
export const REGLEMENT_CHEQUE_MANUEL = 3

export function chequesManuels(r: Rapport | null): { nb: number; total: number } {
  const l = tableau(r?.payments).filter((p) => Number(p.reglementId) === REGLEMENT_CHEQUE_MANUEL)
  return { nb: l.length, total: cents(l.reduce((s, p) => s + (Number(p.montant) || 0), 0)) }
}

/**
 * Bons — nombre et total PAR REGROUPEMENT. Les regroupements ne sont pas
 * réécrits ici : ils viennent de `computeRemiseMensuelle`, qui range déjà
 * Catalina avec Sogec et laisse TR et CAP dehors. Recopier la liste ferait
 * naître une seconde vérité.
 */
export function bonsParGroupe(date: string, r: Rapport | null): LigneDictee[] {
  const remise = computeRemiseMensuelle([{ date, rapport: r ?? {} }]) as {
    bons: { byType?: Record<string, { mnt: number; nb: number }>
            bonCatalina: { mnt: number; nb: number }
            bonFournisseur: { mnt: number; nb: number }
            bonMagasin: { mnt: number; nb: number }
            unclassified: unknown[] }
  }
  const b = remise.bons
  const lignes: LigneDictee[] = []

  // `byType` porte les BON DIVERS classés (sogec, kadéos…). Catalina est un
  // règlement à part (17) que la remise range AVEC Sogec : on l'y ajoute donc
  // ici aussi, plutôt que d'en faire une ligne que COL MNC n'attend pas.
  const parType = new Map<string, { mnt: number; nb: number }>()
  for (const [t, v] of Object.entries(b.byType ?? {})) parType.set(t, { ...v })
  if (b.bonCatalina.nb > 0) {
    const s = parType.get('sogec') ?? { mnt: 0, nb: 0 }
    parType.set('sogec', { mnt: cents(s.mnt + b.bonCatalina.mnt), nb: s.nb + b.bonCatalina.nb })
  }
  for (const [t, v] of parType) {
    lignes.push({
      cle: NOM_BON[t] ?? t,
      compte: `${v.nb} bon${v.nb > 1 ? 's' : ''}`,
      source: t === 'sogec' && b.bonCatalina.nb > 0 ? `dont ${b.bonCatalina.nb} Catalina` : undefined,
      valeur: cents(v.mnt),
    })
  }
  if (b.bonFournisseur.nb > 0) {
    lignes.push({ cle: 'Bon fournisseur', compte: `${b.bonFournisseur.nb} bons`, valeur: cents(b.bonFournisseur.mnt) })
  }
  if (b.bonMagasin.nb > 0) {
    lignes.push({ cle: 'Bon magasin', compte: `${b.bonMagasin.nb} bons`, valeur: cents(b.bonMagasin.mnt) })
  }
  return lignes.sort((a, x) => x.valeur - a.valeur)
}

const NOM_BON: Record<string, string> = { sogec: 'Sogec', kadeos: 'Kadéos' }

/** Un bon divers non classé ne compte nulle part : il faut le dire. */
export function bonsNonClasses(date: string, r: Rapport | null): number {
  const remise = computeRemiseMensuelle([{ date, rapport: r ?? {} }]) as {
    bons: { unclassified: unknown[] }
  }
  return remise.bons.unclassified.length
}

export const cleBillet = BILLET_KEY
